import {
  dashboardRouteSearchSchema,
  type DashboardRouteSearch,
} from '../../dashboard/dashboardRouteSearch';
import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  CampaignStatus,
  defaultLineItemsFilter,
  LineItemDeliveryStatus,
  type LineItemsFilter,
} from './model';

export type LineItemsRouteSearch = DashboardRouteSearch & {
  agencies?: string;
  lineSearch?: string;
  lineStatuses?: string;
  deliveryStatuses?: string;
  linePage?: number;
  lineSize?: number;
};

export type LineItemsRouteFilters = Pick<
  LineItemsFilter,
  'name' | 'status' | 'deliveryStatus' | 'page' | 'size'
> & { agencyIds?: string[] };

export function lineItemsRouteSearchSchema(search: Record<string, unknown>): LineItemsRouteSearch {
  const agencies = optionalTrimmedString(search['agencies']);
  const lineSearch = optionalTrimmedString(search['lineSearch']);
  const lineStatuses = optionalString(search['lineStatuses']);
  const deliveryStatuses = optionalString(search['deliveryStatuses']);
  const linePage = optionalPositiveInteger(search['linePage']);
  const lineSize = optionalPositiveInteger(search['lineSize']);
  return {
    ...dashboardRouteSearchSchema(search),
    ...(agencies ? { agencies } : {}),
    ...(lineSearch ? { lineSearch } : {}),
    ...(lineStatuses !== undefined ? { lineStatuses } : {}),
    ...(deliveryStatuses !== undefined ? { deliveryStatuses } : {}),
    ...(linePage ? { linePage } : {}),
    ...(lineSize ? { lineSize } : {}),
  };
}

export function parseLineItemsRouteSearch(search: LineItemsRouteSearch): LineItemsRouteFilters {
  return {
    ...(search.agencies
      ? {
          agencyIds: search.agencies
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean),
        }
      : {}),
    name: search.lineSearch ?? defaultLineItemsFilter.name,
    status: parseEnumList(search.lineStatuses, CampaignStatus, defaultLineItemsFilter.status),
    deliveryStatus: parseEnumList(
      search.deliveryStatuses,
      LineItemDeliveryStatus,
      defaultLineItemsFilter.deliveryStatus,
    ),
    page: search.linePage ? search.linePage - 1 : defaultLineItemsFilter.page,
    size: search.lineSize ?? defaultLineItemsFilter.size,
  };
}

export function serializeLineItemsRouteSearch(
  filters: LineItemsRouteFilters,
): LineItemsRouteSearch {
  return {
    ...(filters.agencyIds?.length ? { agencies: filters.agencyIds.join(',') } : {}),
    ...(filters.name.trim() ? { lineSearch: filters.name.trim() } : {}),
    ...(sameValues(filters.status, defaultLineItemsFilter.status)
      ? {}
      : { lineStatuses: filters.status.join(',') }),
    ...(sameValues(filters.deliveryStatus, defaultLineItemsFilter.deliveryStatus)
      ? {}
      : { deliveryStatuses: filters.deliveryStatus.join(',') }),
    ...(filters.page ? { linePage: filters.page + 1 } : {}),
    ...(filters.size !== defaultLineItemsFilter.size ? { lineSize: filters.size } : {}),
  };
}

function optionalString(value: unknown) {
  return typeof value === 'string' ? value.trim() : undefined;
}

function parseEnumList<TValue extends string>(
  value: string | undefined,
  enumObject: Record<string, TValue>,
  fallback: readonly TValue[],
) {
  if (value === undefined) return [...fallback];
  if (!value) return [];
  const allowed = new Set(Object.values(enumObject));
  return value.split(',').filter((item): item is TValue => allowed.has(item as TValue));
}

function sameValues<TValue>(left: readonly TValue[], right: readonly TValue[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}
